import { CrossFrameUtils } from '@src/utils/CrossFrameUtils';
import { PhotoUploadProgress } from '@services/camera/CameraTypes';

/**
 * Method name IhmAssociateToolsBase pushes per-photo batch progress on.
 * Must match the ATBase cameraBatchCaptureListener's BATCH_EVENTS_METHOD.
 */
const BATCH_EVENTS_METHOD = 'cameraBatchCaptureEvents';

/**
 * Tool-frame receiving side of the batch progress channel.
 *
 * The batch call itself is ONE long-lived request whose response arrives only
 * at session end; per-photo progress cannot ride it (one request, one
 * response), so ATBase pushes each event as a fresh host->tool request on
 * BATCH_EVENTS_METHOD. This channel routes those pushes to the session's
 * `onPhotoUploaded` listener by sessionId — the same shape as the
 * BluetoothLowEnergy event channel.
 *
 * The response this listener returns doubles as the acknowledgment ATBase's
 * push transport observes, so a navigated-away tool surfaces host-side as
 * unacknowledged pushes rather than silent loss.
 */
type ProgressListener = (progress: PhotoUploadProgress) => void;

const listenersBySession = new Map<string, ProgressListener>();
let registered = false;

function ensureRegistered(): void {
    if (registered) {
        return;
    }
    registered = true;
    CrossFrameUtils.registerListener(BATCH_EVENTS_METHOD, async (message: any) => {
        const progress = message?.data;
        const listener = progress?.sessionId ? listenersBySession.get(progress.sessionId) : undefined;
        if (!listener) {
            // Late push after the session settled/unsubscribed — acknowledged and dropped.
            return { acknowledged: false };
        }
        try {
            listener({
                photoIndex: progress.photoIndex,
                totalPhotos: progress.totalPhotos,
                status: progress.status,
            });
        } catch (error) {
            // A throwing listener must not disturb the session (or the ack).
            console.error('onPhotoUploaded listener threw; continuing', error);
        }
        return { acknowledged: true };
    });
}

/** Routes a session's progress pushes to its listener until unsubscribed. */
export function subscribeBatchProgress(sessionId: string, listener: ProgressListener): void {
    ensureRegistered();
    listenersBySession.set(sessionId, listener);
}

/** Stops routing for a settled session; late pushes are acknowledged and dropped. */
export function unsubscribeBatchProgress(sessionId: string): void {
    listenersBySession.delete(sessionId);
}
